document.addEventListener('DOMContentLoaded', () => {
  initHeaderSticky();
  initSwipers();
  initVideoTogglers();
  initMegaMenuTransitions();
  initMegaMenuMedia();
  initExpandableText();
  initMobileMenuFocusTrap();

  if (window.ThemeConfig?.isDesignMode) {
    document.addEventListener('shopify:section:load', () => {
      initSwipers();
      initHeaderSticky();
      initMegaMenuTransitions();
      initMegaMenuMedia();
      initExpandableText();
      initMobileMenuFocusTrap();
    });
  }
});

/**
 * Mega menu promo media
 * Slots render without a src (snippets/mega-promo-image.liquid) and their videos with
 * preload="none", so a closed menu costs nothing. They come in on hover / menu open, and
 * whatever is left is picked up once the page goes idle.
 */
const initMegaMenuMedia = () => {
  const header = document.querySelector('#gz-header');
  if (!header) return;

  const hydrate = (root) => {
    root.querySelectorAll('img[data-src]').forEach(img => {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      delete img.dataset.src;
      delete img.dataset.srcset;
    });

    root.querySelectorAll('video[preload="none"]').forEach(video => {
      video.preload = 'metadata';
      video.load();
    });
  };

  header.querySelectorAll('.js-mega-hover').forEach(mega => {
    mega.addEventListener('mouseenter', () => hydrate(mega), { once: true });
    mega.addEventListener('focusin', () => hydrate(mega), { once: true });
  });

  const toggle = document.getElementById('mobile-menu-toggle');
  const overlay = document.getElementById('mobile-overlay');
  if (toggle && overlay) {
    toggle.addEventListener('change', () => {
      if (toggle.checked) hydrate(overlay);
    }, { once: true });
  }

  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 2000));
  const prefetch = () => idle(() => hydrate(header));
  if (document.readyState === 'complete') prefetch();
  else window.addEventListener('load', prefetch, { once: true });
};

/**
 * assets/global.js
 * Smart Mega Menu Transitions
 */
const initMobileMenuFocusTrap = () => {
  const toggle = document.getElementById('mobile-menu-toggle');
  const container = document.getElementById('mobile-overlay');

  if (!toggle || !container || !window.FocusTrap) return;

  const focusTrap = new FocusTrap(container, {
    onEscape: () => {
      toggle.checked = false;
      toggle.dispatchEvent(new Event('change'));
      focusTrap.deactivate();
    }
  });

  toggle.addEventListener('change', () => {
    if (toggle.checked) {
      document.body.classList.add('overflow-hidden');
      focusTrap.activate();
    } else {
      document.body.classList.remove('overflow-hidden');
      focusTrap.deactivate();
    }
  });
};

const initMegaMenuTransitions = () => {
  const header = document.querySelector('#gz-header');
  if (!header) return;
  const megas = header.querySelectorAll('.js-mega-hover');
  let globalExitTimeout;
  let switchingTimeout;

  // Track manual pauses on mega menu videos
  const videos = header.querySelectorAll('.js-mega-video');
  videos.forEach(video => {
    video.isScriptPausing = false;
    video.isScriptPlaying = false;

    video.addEventListener('play', () => {
      if (!video.isScriptPlaying) {
        delete video.dataset.manualPause;
      }
    });
  });

  // Handle custom video controls
  const videoContainers = header.querySelectorAll('.js-mega-video-container');
  videoContainers.forEach(container => {
    const video = container.querySelector('.js-mega-video');
    const pauseBtn = container.querySelector('.js-mega-video-pause');
    const playOverlay = container.querySelector('.js-mega-video-play-overlay');

    if (!video) return;

    // Toggle play when clicking the container IF paused
    container.addEventListener('click', (e) => {
      // Do not intercept click if wrapped in a link to allow navigation
      if (container.closest('a')) {
        return;
      }

      if (video.paused) {
        e.preventDefault();
        e.stopPropagation();
        delete video.dataset.manualPause; // Clear manual pause on click play
        video.isScriptPlaying = true;
        video.play().finally(() => {
          video.isScriptPlaying = false;
        });
      }
    });

    // Play/Pause button logic
    if (pauseBtn) {
      pauseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (video.paused) {
          delete video.dataset.manualPause;
          video.isScriptPlaying = true;
          video.play().finally(() => {
            video.isScriptPlaying = false;
          });
        } else {
          video.dataset.manualPause = "true"; // Explicitly set manual pause
          video.isScriptPausing = true;
          video.pause();
          setTimeout(() => video.isScriptPausing = false, 50);
        }
      });
    }

    // Update overlay visibility
    const updateOverlay = () => {
      if (playOverlay) {
        if (video.paused) {
          playOverlay.classList.remove('opacity-0', 'invisible');
          playOverlay.classList.add('opacity-100', 'visible');
        } else {
          playOverlay.classList.add('opacity-0', 'invisible');
          playOverlay.classList.remove('opacity-100', 'visible');
        }
      }

      if (pauseBtn) {
        const iconPlay = pauseBtn.querySelector('.js-icon-play');
        const iconPause = pauseBtn.querySelector('.js-icon-pause');

        if (video.paused) {
          if (iconPlay) iconPlay.classList.remove('!hidden');
          if (iconPause) iconPause.classList.add('!hidden');
        } else {
          if (iconPlay) iconPlay.classList.add('!hidden');
          if (iconPause) iconPause.classList.remove('!hidden');
        }
      }
    };

    video.addEventListener('play', updateOverlay);
    video.addEventListener('pause', updateOverlay);

    // Initial state check
    updateOverlay();
  });

  const pauseVideos = (mega) => {
    mega.querySelectorAll('.js-mega-video').forEach(video => {
      video.isScriptPausing = true;
      video.pause();
      setTimeout(() => video.isScriptPausing = false, 50);
    });
  };

  const playVideos = (mega) => {
    if (window.innerWidth < 1024) return; // Desktop panels only, mobile is handled by gz-mobile-nav

    mega.querySelectorAll('.js-mega-video').forEach(video => {
      if (video.dataset.manualPause !== "true") {
        video.isScriptPlaying = true;
        video.play().catch(() => {
          // Fallback if play fails
        }).finally(() => {
          video.isScriptPlaying = false;
        });
      }
    });
  };

  // Panel fades, background and items slide - all three on the same clock.
  const OPEN_MS = 250;
  const animatedParts = (m) => [
    m.querySelector('.js-mega-panel'),
    m.querySelector('.js-mega-bg'),
    m.querySelector('.js-mega-items'),
  ].filter(Boolean);

  const resetSwitch = () => megas.forEach(m => {
    m.classList.remove('is-leaving');
    animatedParts(m).forEach(el => { el.style.transitionDelay = ''; });
  });

  let openedAt = 0;

  let playTimeout;

  megas.forEach(mega => {
    mega.addEventListener('mouseenter', () => {
      mega.dataset.isClosing = "false";

      clearTimeout(globalExitTimeout);
      clearTimeout(playTimeout);

      const openMega = Array.from(megas).find(m => m !== mega && m.classList.contains('is-active'));
      resetSwitch();

      if (openMega) {
        // A negative delay seeks into the transition, so the new menu picks the slide up
        // where the old one left it - and once that slide is done, seeking past its end
        // lands the new menu at once.
        const elapsed = Math.min(performance.now() - openedAt, OPEN_MS);
        animatedParts(mega).forEach(el => {
          el.style.transitionDelay = `-${Math.round(elapsed)}ms`;
        });
        openMega.classList.add('is-leaving');
        clearTimeout(switchingTimeout);
        switchingTimeout = setTimeout(resetSwitch, OPEN_MS + 50);
      } else {
        openedAt = performance.now();
      }

      header.classList.add('mega-active');

      megas.forEach(m => {
        if (m !== mega && m.classList.contains('is-active')) {
          m.classList.remove('is-active');
          pauseVideos(m);
        }
      });

      mega.classList.add('is-active');
      const trigger = mega.querySelector('button[aria-haspopup="true"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Debounce video play
      playTimeout = setTimeout(() => {
        if (mega.classList.contains('is-active')) {
          playVideos(mega);
        }
      }, 500);
    });

    mega.addEventListener('mouseleave', () => {
      clearTimeout(playTimeout);
      globalExitTimeout = setTimeout(() => {
        mega.dataset.isClosing = "true";

        mega.classList.remove('is-active');
        const trigger = mega.querySelector('button[aria-haspopup="true"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');

        header.classList.remove('mega-active');
        resetSwitch();
        pauseVideos(mega);

        // Clear focus if an element inside the mega menu (like play/pause button) is focused
        // This prevents the CSS :focus-within from keeping the menu open after mouseleave
        if (mega.contains(document.activeElement)) {
          document.activeElement.blur();
        }

        setTimeout(() => {
          mega.dataset.isClosing = "false";
        }, 200);

      }, 150);
    });
  });

  header.addEventListener('mouseleave', () => {
    clearTimeout(globalExitTimeout);
    globalExitTimeout = setTimeout(() => {
      megas.forEach(m => {
        if (m.classList.contains('is-active')) {
          m.classList.remove('is-active');
          const trigger = m.querySelector('button[aria-haspopup="true"]');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
          pauseVideos(m);

          if (m.contains(document.activeElement)) {
            document.activeElement.blur();
          }
        }
      });
      header.classList.remove('mega-active');
      resetSwitch();
    }, 200);
  });

  // Ensure menu closes if mouse leaves the browser window entirely
  document.addEventListener('mouseleave', () => {
    clearTimeout(globalExitTimeout);
    megas.forEach(m => {
      if (m.classList.contains('is-active')) {
        m.classList.remove('is-active');
        const trigger = m.querySelector('button[aria-haspopup="true"]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        pauseVideos(m);
      }
    });
    header.classList.remove('mega-active');
    resetSwitch();
  });
};

/**
 * Dynamic Header Scroll Manager for multiple Hero Banners
 */
const initHeaderSticky = () => {
  const header = document.querySelector('#gz-header');
  const banners = document.querySelectorAll('.gz-hero-banner');


  const mobileOverlay = document.getElementById('mobile-overlay');

  const updateHeaderPosition = () => {
    const currentScrollY = window.scrollY;
    const announcementBar = document.querySelector('gz-announcement-bar');
    const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;

    if (header && header.parentElement) {
      header.parentElement.classList.add('gz-sticky-wrapper');
    }

    const isOutOfFlow = window.getComputedStyle(header).position === 'fixed' || window.getComputedStyle(header).position === 'absolute';
    const headerTop = isOutOfFlow ? Math.max(0, announcementHeight - currentScrollY) : 0;
    header.style.top = headerTop + 'px';

    const headerHeight = window.innerWidth < 1024 ? 80 : header.offsetHeight;
    if (mobileOverlay) {
      mobileOverlay.style.top = headerTop + 'px';
      mobileOverlay.style.paddingTop = headerHeight + 'px';
      mobileOverlay.style.bottom = '0px';
    }
  };

  updateHeaderPosition();

  // Top up the first section's clearance if it starts underneath the header. Idempotent and
  // safe to call repeatedly (design-mode re-inits, webfont-driven height changes, resize):
  // it always measures against the section's *original* CSS padding-top (cached on first run
  // in a data attribute), never against its own previous correction, so re-running never
  // compounds. getBoundingClientRect().top is the element's border-box edge and ignores its
  // own padding, so we add the cached base padding back in to get the actual content start.
  const applyHeaderClearance = () => {
    if (header.getAttribute('data-header-scope') !== 'auto') return;
    const main = document.getElementById('MainContent');
    // main.firstElementChild is Shopify's auto-generated .shopify-section wrapper, which always
    // has zero padding of its own - the template's real top padding (page-section, search-page,
    // etc.) lives one level deeper.
    const wrapper = main && main.firstElementChild;
    const isHero = !!(wrapper && wrapper.querySelector('.gz-hero-banner'));
    // The header overlaps a hero, unless it's always white: then the hero moves below it as a
    // whole, through its wrapper, since its media fill the hero's own box padding included
    if (isHero && header.dataset.solid !== 'true') return;
    const target = isHero ? wrapper : wrapper && (wrapper.firstElementChild || wrapper);
    if (!target || !wrapper) return;

    if (target.dataset.gzBasePad === undefined) {
      target.dataset.gzBasePad = parseFloat(window.getComputedStyle(target).paddingTop) || 0;
    }
    const basePad = parseFloat(target.dataset.gzBasePad);
    // Both measured in document coordinates, as they are at the top of the page: the clearance
    // must not depend on the scroll position. A viewport rect taken while scrolled (iOS Safari
    // fires resize whenever its toolbar collapses/expands) made the shortfall grow by scrollY,
    // adding a viewport-sized blank above the content and collapsing the hero.
    const contentTop = target.getBoundingClientRect().top + window.scrollY + basePad;
    // Where the header lands at the top of the page: below the announcement bar
    const announcementBar = document.querySelector('gz-announcement-bar');
    const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
    const headerBottom = announcementHeight + header.offsetHeight;
    const shortfall = Math.ceil(headerBottom - contentTop);
    const padding = basePad + Math.max(0, shortfall);
    target.style.paddingTop = padding + 'px';
    // The hero shortens by the same amount to stay within the viewport, see src/tw-style.css
    if (isHero) document.documentElement.style.setProperty('--gz-hero-clearance', padding + 'px');
  };

  applyHeaderClearance();
  // Header height can change once webfonts finish loading (fallback vs. final font metrics) -
  // recompute after that settles.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(applyHeaderClearance);
  }
  // Width only: header height doesn't depend on the viewport height, and mobile browsers fire
  // resize on every toolbar show/hide while scrolling
  let clearanceWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === clearanceWidth) return;
    clearanceWidth = window.innerWidth;
    applyHeaderClearance();
  });

  requestAnimationFrame(() => {
    header.style.opacity = '1';
  });

  // Bars ([data-nav-sticky-layer]) sit at top: var(--gz-header-offset). Chrome doesn't repaint
  // a stuck bar while its top transitions, so the offset jumps and the bars slide (FLIP).
  let headerOffset = null;
  const subNavChecks = [];
  const headerHeight = () => (window.innerWidth < 1024 ? 80 : header.offsetHeight);
  const syncHeaderOffset = () => {
    const isHidden = header.getAttribute('data-hidden') === 'true';
    const offset = isHidden ? 0 : headerHeight();
    if (offset === headerOffset) return; // a :root write restyles the whole page
    const isFirstRun = headerOffset === null;
    headerOffset = offset;

    const bars = isFirstRun ? [] : Array.from(document.querySelectorAll('[data-nav-sticky-layer]'));
    // Visual position, running slide included
    const before = bars.map(bar => bar.getBoundingClientRect().top);
    bars.forEach(bar => {
      bar.getAnimations().forEach(a => { if (a.id === 'gz-header-follow') a.cancel(); });
      bar.classList.remove('is-following-header');
    });

    document.documentElement.style.setProperty('--gz-header-offset', offset + 'px');
    // Sub-navs switch fixed state for the new line before being measured
    subNavChecks.forEach(check => check());

    // Header's own timing: reversed halfway, its transition is shorter than 300ms
    const headerSlide = header.getAnimations().find(a => a.transitionProperty === 'translate');
    const { duration, easing } = headerSlide
      ? headerSlide.effect.getTiming()
      : { duration: 300, easing: 'cubic-bezier(0, 0, 0.2, 1)' };

    bars.forEach((bar, i) => {
      const delta = before[i] - bar.getBoundingClientRect().top;
      if (Math.abs(delta) < 0.5) return;
      // Covers the seam, see src/tw-style.css
      bar.classList.add('is-following-header');
      const slide = bar.animate(
        [{ transform: `translateY(${delta}px)` }, { transform: 'translateY(0)' }],
        { id: 'gz-header-follow', duration, easing }
      );
      // Cancelled slides reject: the class stays for the next one
      slide.finished.then(() => bar.classList.remove('is-following-header'), () => {});
    });
  };

  // Near its container's end a stuck bar can't drop by the header height and would lag behind
  // it: the header waits until there's room.
  const barsHaveRoom = () => {
    const shift = headerHeight() - (headerOffset || 0);
    if (shift <= 0) return true;
    return Array.from(document.querySelectorAll('[data-nav-sticky-layer]')).every(bar => {
      if (!bar.offsetHeight) return true;
      const barStyle = getComputedStyle(bar);
      // Only sticky bars are held back by their container
      if (barStyle.position !== 'sticky') return true;
      const line = parseFloat(barStyle.top);
      // Sticky position, without the running slide
      const top = bar.getBoundingClientRect().top - new DOMMatrixReadOnly(barStyle.transform).m42;
      // Already pushed out, or in flow below the new line: won't move
      if (top < line - 0.5 || top > line + shift - 0.5) return true;
      const box = bar.parentElement;
      const boxStyle = getComputedStyle(box);
      const boxBottom = box.getBoundingClientRect().bottom
        - parseFloat(boxStyle.paddingBottom) - parseFloat(boxStyle.borderBottomWidth);
      return boxBottom - bar.offsetHeight >= line + shift - 0.5;
    });
  };

  syncHeaderOffset();
  window.addEventListener('resize', syncHeaderOffset);

  // Sub-navs stick via JS: their section is only as tall as they are.
  const setupSubNavSticky = () => {
    const subNavs = document.querySelectorAll('.gz-menu-sub-nav');
    subNavs.forEach(nav => {
      if (nav.dataset.stickyInitialized) return;
      nav.dataset.stickyInitialized = 'true';

      const anchor = document.createElement('div');
      anchor.style.display = 'none';
      nav.parentNode.insertBefore(anchor, nav);

      const checkSticky = () => {
        const isFixed = anchor.style.display === 'block';
        // Placeholder when fixed, else the nav without the running slide
        const top = isFixed
          ? anchor.getBoundingClientRect().top
          : nav.getBoundingClientRect().top - new DOMMatrixReadOnly(getComputedStyle(nav).transform).m42;

        if (top <= (headerOffset || 0)) {
          if (!isFixed) {
            anchor.style.height = nav.offsetHeight + 'px';
            anchor.style.display = 'block';
            nav.classList.add('!fixed', '!left-0', 'w-full', 'z-[40]', 'bg-white');
            nav.style.top = 'var(--gz-header-offset)';
          }
        } else if (isFixed) {
          anchor.style.display = 'none';
          nav.classList.remove('!fixed', '!left-0', 'w-full', 'z-[40]', 'bg-white');
          nav.style.top = '';
        }
      };

      subNavChecks.push(checkSticky);
      window.addEventListener('scroll', checkSticky, { passive: true });
      window.addEventListener('resize', checkSticky, { passive: true });
      checkSticky(); // Initial check
    });
  };

  setupSubNavSticky();

  let lastScrollY = window.scrollY;
  window.addEventListener('scroll', () => {
    updateHeaderPosition();

    const currentScrollY = window.scrollY;
    // An open mega menu hangs off the header, so hiding one strands the other.
    const isMenuOpen = document.getElementById('mobile-menu-toggle')?.checked
      || header.classList.contains('mega-active');

    if (currentScrollY <= 100 || isMenuOpen) {
      header.setAttribute('data-hidden', 'false');
    } else if (currentScrollY > lastScrollY) {
      header.setAttribute('data-hidden', 'true');
    } else if (header.getAttribute('data-hidden') === 'false' || barsHaveRoom()) {
      header.setAttribute('data-hidden', 'false');
    }
    syncHeaderOffset();

    lastScrollY = currentScrollY;
  }, { passive: true });

  // Always white: the scrolled look from the start, whatever sits under the header
  if (header.dataset.solid === 'true') {
    header.setAttribute('data-active', 'true');
    return;
  }

  if (banners.length === 0) {
    if (header.hasAttribute('data-active')) {
      if (header.getAttribute('data-header-scope') === 'managed') {
        const announcementBar = document.querySelector('gz-announcement-bar');
        const announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;

        const updateManagedHeaderActive = () => {
          const isMenuOpen = document.getElementById('mobile-menu-toggle')?.checked;
          if (window.scrollY > announcementHeight || isMenuOpen) {
            header.setAttribute('data-active', 'true');
          } else {
            header.setAttribute('data-active', 'false');
          }
        };

        updateManagedHeaderActive();
        window.addEventListener('scroll', updateManagedHeaderActive, { passive: true });
        document.getElementById('mobile-menu-toggle')?.addEventListener('change', updateManagedHeaderActive);
      } else {
        header.setAttribute('data-active', 'true');
      }
    }
    return;
  }
  let activeBanners = new Set();
  let headerObserver;
  const createObserver = () => {
    const headerHeight = header.offsetHeight;
    const bottomMargin = window.innerHeight - headerHeight;

    const observerOptions = {
      root: null,
      threshold: 0,
      rootMargin: `0px 0px -${bottomMargin}px 0px`
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          activeBanners.add(entry.target);
        } else {
          activeBanners.delete(entry.target);
        }
      });

      if (header.hasAttribute('data-active')) {
        if (activeBanners.size > 0) {
          header.setAttribute('data-active', 'false');
        } else {
          header.setAttribute('data-active', 'true');
        }
      }
    }, observerOptions);
    banners.forEach(banner => observer.observe(banner));
    return observer;
  };
  headerObserver = createObserver();
  window.addEventListener('resize', () => {
    clearTimeout(window.headerResizeTimer);
    window.headerResizeTimer = setTimeout(() => {
      if (headerObserver) headerObserver.disconnect();
      headerObserver = createObserver();
    }, 100);
  });
};

/**
 * Swiper Global Assets Loader
 * Kept outside the init function to avoid multiple downloads in the Theme Editor
 */
let isSwiperLoaded = false;
let swiperLoadPromise = null;

const loadSwiperAssets = () => {
  if (swiperLoadPromise) return swiperLoadPromise;

  swiperLoadPromise = new Promise((resolve, reject) => {
    const cssUrl = window.ThemeConfig?.swiperCssUrl;
    const jsUrl = window.ThemeConfig?.swiperJsUrl;

    if (!cssUrl || !jsUrl) {
      console.error('Swiper asset URLs are missing in window.ThemeConfig');
      return reject();
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = jsUrl;
    script.async = true;
    script.onload = () => {
      isSwiperLoaded = true;
      resolve(window.Swiper);
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return swiperLoadPromise;
};

const initSingleSwiper = (swiperNode, SwiperConstructor) => {
  if (swiperNode.classList.contains('swiper-initialized')) return;
  const configString = swiperNode.getAttribute('data-swiper-config');
  const config = configString ? JSON.parse(configString) : {};
  new SwiperConstructor(swiperNode, config);
};

/**
 * Swiper Initialization Manager
 */
const initSwipers = () => {
  const isDesktop = window.innerWidth >= 1024;
  const swiperElements = Array.from(
    document.querySelectorAll('.swiper[data-swiper-init]:not(.swiper-initialized)')
  ).filter(el => {
    // Skip mobile-only swipers on desktop (they display as CSS grid instead)
    if (isDesktop && el.dataset.swiperMobileOnly === 'true') return false;
    return true;
  });
  if (swiperElements.length === 0) return;

  if (window.ThemeConfig?.isDesignMode) {
    loadSwiperAssets().then((SwiperConstructor) => {
      swiperElements.forEach(el => initSingleSwiper(el, SwiperConstructor));
    });
    return;
  }

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const swiperNode = entry.target;
        obs.unobserve(swiperNode);

        loadSwiperAssets().then((SwiperConstructor) => {
          initSingleSwiper(swiperNode, SwiperConstructor);
        });
      }
    });
  }, { rootMargin: '200px' });

  swiperElements.forEach(el => observer.observe(el));
};

// Bridge for cross-file Swiper re-init (used by collection.js and collection-editorial.js)
window.gzInitSwipers = initSwipers;

/**
 * Global Video Toggle Manager
 */
const initVideoTogglers = () => {
  document.addEventListener('click', (event) => {
    const toggleBtn = event.target.closest('.js-video-toggle');
    if (!toggleBtn) return;
    event.preventDefault();
    event.stopPropagation();

    const container = toggleBtn.closest('.gz-hero-banner, .js-video-container, section');
    if (!container) return;

    const videos = Array.from(container.querySelectorAll('video'));
    if (videos.length === 0) return;

    const isPaused = videos[0].paused;

    // Toggle playback
    videos.forEach(video => {
      if (isPaused) {
        video.play();
      } else {
        video.pause();
      }
    });

    // Editorial Card Play/Pause Icons
    const centerBtn = container.querySelector('.js-center-play');
    const cornerBtn = container.querySelector('.js-corner-toggle');

    if (isPaused) {
      // Now playing
      if (centerBtn) centerBtn.classList.add('!hidden');
      if (cornerBtn) cornerBtn.classList.remove('!hidden');
    } else {
      // Now paused
      if (centerBtn) centerBtn.classList.remove('!hidden');
      if (cornerBtn) cornerBtn.classList.add('!hidden');
    }

    // Also sync icons inside buttons if they exist
    const allButtons = container.querySelectorAll('.js-video-toggle');
    allButtons.forEach(btn => {
      const iconPause = btn.querySelector('.js-icon-pause');
      const iconPlay = btn.querySelector('.js-icon-play');

      if (isPaused) {
        if (iconPause) iconPause.classList.remove('!hidden');
        if (iconPlay) iconPlay.classList.add('!hidden');
      } else {
        if (iconPause) iconPause.classList.add('!hidden');
        if (iconPlay) iconPlay.classList.remove('!hidden');
      }
    });
  });
};

/**
 * Footer Components
 */
if (!customElements.get('gz-footer-accordion')) {
  class GzFooterAccordion extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-toggle]');
      this.content = this.querySelector('[data-content]');
      this.icon = this.btn.querySelector('svg');
      this.isOpen = false;

      if (!this.btn || !this.content) return;

      this.btn.addEventListener('click', () => {
        if (window.innerWidth >= 768) return;

        this.isOpen = !this.isOpen;
        if (this.isOpen) {
          this.content.style.maxHeight = this.content.scrollHeight + 'px';
          this.content.style.opacity = '1';
          if (this.icon) this.icon.style.transform = 'rotate(180deg)';
        } else {
          this.content.style.maxHeight = '0px';
          this.content.style.opacity = '0';
          if (this.icon) this.icon.style.transform = 'rotate(0deg)';
        }
      });

      // Reset styles on resize to desktop
      window.addEventListener('resize', () => {
        if (window.innerWidth >= 768) {
          this.content.style.maxHeight = '';
          this.content.style.opacity = '';
          if (this.icon) this.icon.style.transform = '';
          this.isOpen = false;
        }
      });
    }
  }
  customElements.define('gz-footer-accordion', GzFooterAccordion);
}

if (!customElements.get('gz-footer-benefits')) {
  class GzFooterBenefits extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('#benefits-track');
      this.prevBtn = this.querySelector('#benefit-prev');
      this.nextBtn = this.querySelector('#benefit-next');
      this.slides = this.querySelectorAll('.flex-shrink-0');
      this.currentIndex = 0;

      if (!this.track || !this.prevBtn || !this.nextBtn || this.slides.length === 0) return;

      const updateSlider = () => {
        if (window.innerWidth >= 768) {
          this.track.style.transform = '';
        } else {
          this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;
        }
      };

      this.prevBtn.addEventListener('click', () => {
        if (window.innerWidth >= 768) return;
        this.currentIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
        updateSlider();
      });

      this.nextBtn.addEventListener('click', () => {
        if (window.innerWidth >= 768) return;
        this.currentIndex = this.currentIndex === this.slides.length - 1 ? 0 : this.currentIndex + 1;
        updateSlider();
      });

      window.addEventListener('resize', updateSlider);
      updateSlider();
    }
  }
  customElements.define('gz-footer-benefits', GzFooterBenefits);
}

/**
 * Mobile Navigation Manager
 * Handles level switching (inline show/hide) and accordion expansion.
 */
if (!customElements.get('gz-mobile-nav')) {
  class GzMobileNav extends HTMLElement {
    connectedCallback() {
      // Re-init elements
      this._init();

      // Re-bind when menu is toggled via checkbox
      const checkbox = document.getElementById('mobile-menu-toggle');
      if (checkbox) {
        checkbox.addEventListener('change', () => {
          if (!checkbox.checked) {
            document.body.style.overflow = ''; // Sblocca lo scroll istantaneamente
            setTimeout(() => this._closeAll(), 300);
          } else {
            document.body.style.overflow = 'hidden'; // Blocca lo scroll in modo performante
            this._init();
          }
        });

        // Close menu if window is resized to desktop while open
        const mql = window.matchMedia('(min-width: 1024px)');
        mql.addEventListener('change', (e) => {
          if (e.matches && checkbox.checked) {
            checkbox.checked = false;
            this._closeAll();
          }
        });
      }
    }

    _init() {
      this._level1 = this.querySelector('#mobile-level-1');
      this._panels = this.querySelectorAll('.mobile-nav-panel');
      this._bindTriggers();
      this._bindBackButtons();
      this._bindAccordions();
    }

    _bindTriggers() {
      this.querySelectorAll('[data-mobile-trigger]').forEach(btn => {
        btn.onclick = () => {
          const targetId = btn.getAttribute('data-mobile-trigger');
          const panel = document.getElementById(targetId);
          if (!panel) return;

          if (this._level1) this._level1.setAttribute('data-state', 'inactive-left');
          panel.setAttribute('data-state', 'active');
          btn.setAttribute('aria-expanded', 'true');
          this._playVideos(panel);

          const overlay = document.getElementById('mobile-overlay');
          if (overlay) overlay.scrollTop = 0;

          if (!window.ThemeConfig?.menuInteractionAllLevels) return;
          window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
          window.GZAnalyticsQueue.push((GZAnalytics) => {
            GZAnalytics.push(Object.assign({ event: 'menu_interaction' }, GZAnalytics.menuLabels(btn)));
          });
        };
      });
    }

    _bindBackButtons() {
      this.querySelectorAll('[data-mobile-back]').forEach(btn => {
        btn.onclick = () => {
          const panel = btn.closest('.mobile-nav-panel');
          if (panel) {
            panel.setAttribute('data-state', 'inactive-right');
            this._pauseVideos(panel);
            const trigger = this.querySelector(`[data-mobile-trigger="${panel.id}"]`);
            if (trigger) trigger.setAttribute('aria-expanded', 'false');
          }
          if (this._level1) this._level1.setAttribute('data-state', 'active');
        };
      });
    }

    _bindAccordions() {
      this.querySelectorAll('[data-mobile-accordion]').forEach(btn => {
        btn.onclick = () => {
          const content = btn.nextElementSibling;
          if (!content) return;
          const isOpen = content.getAttribute('aria-hidden') === 'false';

          const panel = btn.closest('.mobile-nav-panel');
          panel.querySelectorAll('[data-mobile-accordion]').forEach(otherBtn => {
            if (otherBtn !== btn) {
              const otherContent = otherBtn.nextElementSibling;
              const otherIcon = otherBtn.querySelector('.js-icon');
              if (otherContent) otherContent.setAttribute('aria-hidden', 'true');
              otherBtn.setAttribute('aria-expanded', 'false');
              if (otherIcon) otherIcon.style.transform = 'rotate(0deg)';
            }
          });

          const icon = btn.querySelector('.js-icon');
          if (isOpen) {
            content.setAttribute('aria-hidden', 'true');
            btn.setAttribute('aria-expanded', 'false');
            if (icon) icon.style.transform = 'rotate(0deg)';
          } else {
            content.setAttribute('aria-hidden', 'false');
            btn.setAttribute('aria-expanded', 'true');
            if (icon) icon.style.transform = 'rotate(180deg)';

            if (!window.ThemeConfig?.menuInteractionAllLevels) return;
            window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
            window.GZAnalyticsQueue.push((GZAnalytics) => {
              GZAnalytics.push(Object.assign({ event: 'menu_interaction' }, GZAnalytics.menuLabels(btn)));
            });
          }
        };
      });
    }

    // Mega menu videos start when their panel opens, if the block allows it (mobile_autoplay).
    // A video the user paused stays paused, as on desktop.
    _playVideos(panel) {
      if (!panel.hasAttribute('data-video-autoplay')) return;
      panel.querySelectorAll('.js-mega-video').forEach(video => {
        if (video.dataset.manualPause === 'true') return;
        video.play().catch(() => {});
      });
    }

    _pauseVideos(root) {
      root.querySelectorAll('.js-mega-video').forEach(video => video.pause());
    }

    _closeAll() {
      this._pauseVideos(this);
      if (this._panels) {
        this._panels.forEach(p => {
          if (p.id === 'mobile-level-1') {
            p.setAttribute('data-state', 'active');
          } else {
            p.setAttribute('data-state', 'inactive-right');
          }
        });
      }
      this.querySelectorAll('[data-mobile-accordion]').forEach(btn => {
        btn.setAttribute('aria-expanded', 'false');
        const content = btn.nextElementSibling;
        const icon = btn.querySelector('.js-icon');
        if (content) content.setAttribute('aria-hidden', 'true');
        if (icon) icon.style.transform = 'rotate(0deg)';
      });
    }
  }
  customElements.define('gz-mobile-nav', GzMobileNav);
}

/**
 * GzProductCard - Mini Swiper for Product Grid
 * Handles hover transition from Cover to Hover image
 */
if (!customElements.get('gz-product-card')) {
  class GzProductCard extends HTMLElement {
    connectedCallback() {
      // Show card only when first image is ready
      const firstImage = this.querySelector('img');
      if (firstImage) {
        if (firstImage.complete) {
          this.style.opacity = '1';
        } else {
          firstImage.addEventListener('load', () => {
            this.style.opacity = '1';
          });
        }
      } else {
        this.style.opacity = '1';
      }

      this.swiperNode = this.querySelector('[data-product-card-swiper]');
      if (!this.swiperNode) return;

      this._swiperInitialized = false;

      const isMobile = () => window.innerWidth < 1024;

      // The second image comes down as the card nears the viewport, on both breakpoints,
      // so the first hover or swipe never lands on an image that has not started loading.
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          observer.unobserve(this);
          this.hydrateSlide(1);
          if (isMobile()) this.initSwiper();
        });
      }, { rootMargin: '200px' });
      observer.observe(this);

      if (!isMobile()) {
        // Desktop: hovering is intent to browse the card, so pull the whole set then
        const onFirstHover = () => {
          this.removeEventListener('mouseenter', onFirstHover);
          this.hydrateRest();
          this.initSwiper();
        };
        this.addEventListener('mouseenter', onFirstHover);
      }
    }

    // Slides past the cover ship with data-src only (product-card-slides.liquid)
    hydrateImage(img) {
      if (!img || !img.dataset.src) return;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      delete img.dataset.src;
      delete img.dataset.srcset;
    }

    hydrateSlide(index) {
      const slide = this.querySelectorAll('.swiper-slide')[index];
      if (slide) this.hydrateImage(slide.querySelector('img[data-src]'));
    }

    // Everything left, in one go: slideChange fires once the transition is already
    // running, so hydrating per slide there shows an empty frame.
    hydrateRest() {
      if (this._fullyHydrated) return;
      this._fullyHydrated = true;
      this.querySelectorAll('img[data-src]').forEach(img => this.hydrateImage(img));
    }

    async initSwiper() {
      if (this._swiperInitialized) return;
      this._swiperInitialized = true;

      try {
        const SwiperConstructor = await loadSwiperAssets();
        const progressBar = this.querySelector('.js-card-progress-bar');

        this.swiper = new SwiperConstructor(this.swiperNode, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          nested: true,
          on: {
            init: (swiper) => {
              if (progressBar) {
                const total = swiper.slides.length;
                progressBar.style.width = `${100 / total}%`;
              }
            },
            // The drag begins before any slide is uncovered: pull the rest now
            touchStart: () => this.hydrateRest(),
            slideChange: (swiper) => {
              this.hydrateRest();
              if (progressBar) {
                progressBar.style.transform = `translateX(${swiper.activeIndex * 100}%)`;
              }
            }
          }
        });
      } catch (e) {
        console.error('Error initializing product card swiper:', e);
      }
    }
  }
  customElements.define('gz-product-card', GzProductCard);
}


/**
 * initExpandableText
 * Handles 'Read More' toggles for description blocks.
 * Detects truncation and toggles .is-expanded (collapsed styles in src/tw-style.css).
 */
const initExpandableText = () => {
  document.querySelectorAll('[data-expandable-container]').forEach(container => {
    if (container._expandBound) return;
    container._expandBound = true;

    const text = container.querySelector('[data-expandable-text]');
    const triggers = container.querySelectorAll('[data-expand-trigger]');
    const inlineTrigger = container.querySelector('[data-expand-inline]');
    if (!text || !triggers.length) return;

    // The inline trigger floats at the right edge of the 2nd line: shift it next to the last word
    const alignInlineTrigger = () => {
      if (!inlineTrigger) return;
      inlineTrigger.style.transform = '';
      if (inlineTrigger.offsetParent === null) return;

      const box = inlineTrigger.getBoundingClientRect();
      const range = document.createRange();
      range.selectNodeContents(text);
      let lineEnd = null;
      for (const rect of range.getClientRects()) {
        const onTriggerLine = rect.top >= box.top - 1 && rect.bottom <= box.bottom + 1;
        if (rect.width && onTriggerLine && rect.right <= box.left + 1) {
          lineEnd = Math.max(lineEnd ?? rect.right, rect.right);
        }
      }
      if (lineEnd !== null) inlineTrigger.style.transform = `translateX(${lineEnd - box.left}px)`;
    };

    // Check if text is actually truncated
    const checkTruncation = () => {
      if (container.classList.contains('is-expanded')) return; // Don't check if already expanded

      const isTruncated = text.scrollHeight > text.clientHeight + 2;
      triggers.forEach(trigger => trigger.classList.toggle('hidden', !isTruncated));
      alignInlineTrigger();
    };

    // Only triggers with data-text-* swap label (the inline one is shown collapsed only)
    const setLabels = attr => triggers.forEach(trigger => {
      if (trigger.hasAttribute(attr)) trigger.textContent = trigger.getAttribute(attr);
    });

    // Initial check
    setTimeout(checkTruncation, 200);
    window.addEventListener('resize', checkTruncation);

    const toggle = event => {
      if (!container.classList.contains('is-expanded')) {
        // Expand
        container.classList.add('is-expanded');
        if (container.classList.contains('lg:h-[232px]')) {
          container.classList.remove('lg:h-[232px]');
          container._hadFixedHeight = true;
        }
        if (container.classList.contains('lg:min-h-[232px]')) {
          container.classList.remove('lg:min-h-[232px]');
          container._hadMinHeight = true;
        }
        setLabels('data-text-less');
      } else {
        // Collapse
        container.classList.remove('is-expanded');
        if (container._hadFixedHeight) {
          container.classList.add('lg:h-[232px]');
        }
        if (container._hadMinHeight) {
          container.classList.add('lg:min-h-[232px]');
        }
        setLabels('data-text-more');
        checkTruncation();
      }

      // Inline mode swaps trigger on toggle: keep keyboard focus on the visible one
      if (event.currentTarget.offsetParent === null) {
        [...triggers].find(trigger => trigger.offsetParent !== null)?.focus();
      }
    };
    triggers.forEach(trigger => trigger.addEventListener('click', toggle));
  });
};

/**
 * gz-select - Generic Select wrapper to handle chevron rotation
 */
if (!customElements.get('gz-select')) {
  class GzSelect extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('.js-select');
      this.icon = this.querySelector('.js-chevron');
      if (!this.select || !this.icon) return;

      this.select.addEventListener('click', () => {
        this.icon.classList.toggle('rotate-180');
      });
      this.select.addEventListener('blur', () => {
        this.icon.classList.remove('rotate-180');
      });
      this.select.addEventListener('change', () => {
        this.icon.classList.remove('rotate-180');
      });
    }
  }
  customElements.define('gz-select', GzSelect);
}

/**
 * Header cart counter - kept in sync by every cart mutation (cart page, drawer, add to cart).
 * The header is server-rendered with the current count, so this only patches it in place.
 */
window.updateCartCount = function (count) {
  const value = parseInt(count, 10);
  if (isNaN(value)) return;

  document.querySelectorAll('.gz-cart-icon').forEach((icon) => {
    icon.dataset.cartCount = value;

    // The badge only exists while the cart has items, so an empty cart never shows one
    let badge = icon.querySelector('.gz-cart-icon__badge');
    if (value > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'gz-cart-icon__badge';
        badge.setAttribute('aria-hidden', 'true');
        icon.appendChild(badge);
      }
      badge.textContent = value > 9 ? '9+' : value;
    } else if (badge) {
      badge.remove();
    }

    // The trigger's aria-label carries the count: its inner text is not exposed to AT
    const trigger = icon.closest('a, button');
    const label = icon.dataset.cartLabel;
    if (trigger && label) {
      trigger.setAttribute('aria-label', value > 0 ? label + ' (' + value + ')' : label);
    }
  });
};
